/**
 * Shared retry predicate for TanStack Query.
 *
 * The fetch layer already decides what is worth repeating (see isRetryableError
 * in lib/fetch-utils): only a missing status — a network failure — or a 503.
 * Everything else, 4xx above all, returns the same answer however many times it
 * is asked: a 401 does not grow a session cookie, a 403 does not grant a role.
 * The one exception is `retryServerFaultOnce`, for a read whose failure is read
 * as an answer rather than shown as an error.
 *
 * Queries that set a bare `retry: N` silently overrule that reasoning one layer
 * up and double every failure, which is how a single expired embedded session
 * turned into two rounds of dashboard-wide errors. This predicate keeps the two
 * layers aligned.
 */

import { ApiContractError } from '@/lib/fetch-utils';
import { StaleAccessRevisionError } from '@/lib/access-revision';

const TRANSIENT_STATUS = new Set([503]);

/**
 * Statuses the fetch layer does NOT repeat (`isRetryableError`): the answer of
 * a failing gateway or backend, which a second GET may well not repeat. Only a
 * read whose failure costs more than a spinner asks for them
 * (`retryServerFaultOnce`) — everywhere else a repeat just multiplies load on a
 * backend that is already failing.
 */
const SERVER_FAULT_STATUS = new Set([500, 502, 504]);

/** Attempts to make after the first failure, for genuinely transient errors. */
const MAX_TRANSIENT_RETRIES = 1;

function statusOf(error: unknown): unknown {
  return (error as { status?: unknown } | null)?.status;
}

/**
 * Whether a failure may answer differently next time. The same verdict the
 * predicate below applies per attempt, for callers that schedule their own
 * repeats (the assistant's settlement read).
 */
export function isTransientFailure(error: unknown): boolean {
  // The server answered with a body the client's schema rejects: the same
  // body comes back on every repeat — no status, but no network failure
  // either (owner's review, 17.09).
  if (error instanceof ApiContractError) return false;
  // A late answer of an older access revision (E1.5 PR 4b): asked again, the
  // request leaves with the revision now known and comes back current.
  if (error instanceof StaleAccessRevisionError) return true;
  const status = statusOf(error);
  // No status at all means the request never got an answer (offline, DNS,
  // aborted connection) — the one case where trying again is meaningful.
  if (status === undefined || status === null) return true;

  return typeof status === 'number' && TRANSIENT_STATUS.has(status);
}

export function retryTransientOnly(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_TRANSIENT_RETRIES) return false;
  return isTransientFailure(error);
}

/**
 * The default for queries that do not retry at all (`main.tsx`): a late
 * answer of an older access revision (E1.5 PR 4b) is still asked once more —
 * otherwise the screen would stay in an error the next request would not
 * have.
 */
export function retryStaleAccessOnce(failureCount: number, error: unknown): boolean {
  return (
    failureCount < MAX_TRANSIENT_RETRIES && error instanceof StaleAccessRevisionError
  );
}

/**
 * For a read whose failure is read as an ANSWER, not as an error: the demo
 * status turns a failed read into `includeDemo: 'no'`, and one 502 would empty
 * the dashboards of an org that has only demo data (#1827). It repeats exactly
 * what no other layer repeats — a 500/502/504, once, and the late answer of an
 * older access revision like the client default. A network failure and a 503
 * are the fetch layer's already: asking again here would stack the attempts.
 */
export function retryServerFaultOnce(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_TRANSIENT_RETRIES) return false;
  if (error instanceof StaleAccessRevisionError) return true;
  const status = statusOf(error);
  return typeof status === 'number' && SERVER_FAULT_STATUS.has(status);
}
